import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { CommentModeProvider } from '@/shared/contexts/CommentModeProvider';
import { DemoSpendConfigProvider } from '@/domains/billing/components/layout/DemoSpendConfigProvider';
import { DomainProvider, useDomain } from '@/shared/contexts/DomainProvider';
import { GenericCommentSystem } from '@/shared/components/comments/GenericCommentSystem';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { useSystemUsers } from '@/shared/hooks/useSystemUsers';
import { initializeAuthStore, cleanupAuthStore } from '@/shared/stores/authStore';
import { useUserCacheStore } from '@/shared/stores/userCacheStore';
import ProtectedRoute from '@/domains/admin/components/auth/ProtectedRoute';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { DomainLoadingScreen } from '@/shared/components/patterns/DomainLoadingScreen';
import { LoadingIcon } from '@/shared/components/base/loading-screen';
import { getAllRoutes, getAllRouteDefinitions, getDomainById, DOMAINS, hasAccessToRoute } from '@/domain-registry';
import type { DomainRoute } from '@/domain-registry';
import { useNavigate, matchPath } from 'react-router-dom';
import { analyticsService } from '@/shared/services/analyticsService';
import { ErrorFallback } from '@/shared/components/base/ErrorFallback';
import { TooltipProvider } from '@/shared/components/primitives/Tooltip/Tooltip';

const LLMContextPage = lazy(() => import('./pages/LLMContext'));
const HomePage = lazy(() => import('./domains/home/pages/HomePage'));

// Login component for unauthenticated users
function LoginPage() {
  const { signIn, isLoading, error, clearError } = useAuthUnified();

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
            radial-gradient(circle at 60% 60%, #e8f0fe 0%, transparent 50%),
            linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
          `
        }}
      >
        <div className="backdrop-blur-xl bg-white/10 rounded-3xl shadow-2xl border border-white/60 p-12 max-w-md w-full">
          <LoadingIcon />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
            radial-gradient(circle at 60% 60%, #e8f0fe 0%, transparent 50%),
            linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
          `
        }}
      >
        <div className="backdrop-blur-xl bg-white/10 rounded-3xl shadow-2xl border border-white/30 p-12 max-w-md w-full">
          <div className="bg-red-50/70 backdrop-blur-md border border-red-200/40 rounded-2xl p-5 mb-4">
            <h3 className="text-red-800 font-semibold mb-2">Authentication Error</h3>
            <p className="text-red-600 text-sm mb-3">{error}</p>
            <button
              onClick={clearError}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg text-sm transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundImage: `
          radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
          radial-gradient(circle at 60% 60%, #e8f0fe 0%, transparent 50%),
          linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
        `
      }}
    >
      <div className="backdrop-blur-xl bg-white/60 rounded-3xl shadow-2xl border border-white/40 p-12 max-w-md w-full">
        <div className="text-center mb-10">
          {/* Logo with circular background and proper spacing */}
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" style={{ backgroundColor: '#311c3b' }}>
            <img
              src="/thrive_icon.png"
              alt="Thrive Ops Logo"
              className="w-11 h-11"
              style={{
                position: 'relative',
                top: '2px',    // Adjust vertically: positive = down, negative = up
                left: '1.5px'    // Adjust horizontally: positive = right, negative = left
              }}
            />
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#311c3b' }}>
            Welcome to ThriveOS
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Sign in with your Google Workspace account.
          </p>
        </div>
        
        <button
          onClick={signIn}
          className="w-full text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(135deg, #5b3d72 0%, #704a8b 100%)'
          }}
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>
        
        <div className="mt-8 text-center text-xs text-gray-500">
          <p>Secure authentication powered by Google Workspace</p>
        </div>
      </div>
    </div>
  );
}

// Smart redirect component that preserves domain context
function SmartRedirect() {
  const location = useLocation();
  
  // Extract domain from current URL
  const extractDomainFromPath = (pathname: string): string => {
    const segments = pathname.replace(/^\//, '').split('/');
    const potentialDomain = segments[0];
    
    // Check if it's a valid domain
    if (potentialDomain && DOMAINS[potentialDomain]) {
      return potentialDomain;
    }
    
    // Default to home if no valid domain found
    return 'home';
  };
  
  const currentDomain = extractDomainFromPath(location.pathname);
  const domainConfig = getDomainById(currentDomain);
  
  // Redirect to the current domain's default route, or home if invalid
  const redirectTo = domainConfig?.defaultRoute || '/home';
  
  return <Navigate to={redirectTo} replace />;
}

// Route Tracker component for Google Analytics page view tracking
function RouteTracker() {
  const location = useLocation();
  const { currentDomain } = useDomain();

  useEffect(() => {
    // Track page view on every route change
    analyticsService.logPageView(
      location.pathname,
      currentDomain,
      document.title
    );
  }, [location.pathname, currentDomain]);

  return null; // This component renders nothing
}

// User Tracker component for Google Analytics user identification
function UserTracker() {
  const { effectiveUser } = useAuthUnified();
  const { users: systemUsers } = useSystemUsers();

  useEffect(() => {
    // Set user ID and properties when user is authenticated
    if (effectiveUser && systemUsers.length > 0) {
      // Find the system user data
      const systemUser = systemUsers.find(
        u => u.employee_id.toString() === effectiveUser.employee_id.toString()
      );

      if (systemUser) {
        // Set user ID for cross-session tracking
        analyticsService.setUserId(systemUser.id);

        // Set user properties for segmentation
        analyticsService.setUserProperties({
          user_email: systemUser.email || undefined,
          user_role: systemUser.employee_data?.role_name || undefined,
          current_program: systemUser.employee_setup?.current_program || undefined,
        });
      }
    }
  }, [effectiveUser, systemUsers]);

  return null; // This component renders nothing
}

/**
 * Check if user has access to a route, redirect to billing dashboard if not
 */
function findAccessibleRoute(route: DomainRoute | undefined): string | null {
  if (!route) return null;
  if (hasAccessToRoute(route)) return null; // No redirect needed
  return '/home';
}

// Main app routes component with domain-based routing
function AppRoutes() {
  const { isAuthenticated, isLoading, groupAccess } = useAuthUnified();
  const navigate = useNavigate();
  const location = useLocation();

  // All route definitions (unfiltered) — used for matching/access-check logic
  const allRoutes = React.useMemo(() => getAllRouteDefinitions(), []);

  // Routes the user is permitted to access — used for rendering
  // Depends on groupAccess because getAllRoutes -> hasAccessToRoute reads auth store
  const availableRoutes = React.useMemo(
    () => getAllRoutes(),
    [groupAccess]
  );

  // Check route permissions and redirect if needed
  React.useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const currentRoute = allRoutes.find(r => matchPath(r.path, location.pathname));
    if (currentRoute) {
      const redirectPath = findAccessibleRoute(currentRoute);
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [location.pathname, isAuthenticated, isLoading, allRoutes, navigate, groupAccess]);

  // Show loading screen while auth is initializing
  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, #FBFAE9 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, #FEF5F1 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, #f3e2f0 0%, transparent 50%),
            radial-gradient(circle at 60% 60%, #e8f0fe 0%, transparent 50%),
            linear-gradient(180deg, #f2f0f9 0%, #f7f9f0 100%)
          `
        }}
      >
        <LoadingIcon />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Routes>
      {/* Login redirect for authenticated users */}
      <Route path="/login" element={<Navigate to="/home" replace />} />
      
      {/* LLM context page - no layout wrapper, plain text for agent consumption */}
      <Route
        path="/llm"
        element={
          <Suspense fallback={<div style={{ padding: '2rem', fontFamily: 'monospace' }}>Loading...</div>}>
            <LLMContextPage />
          </Suspense>
        }
      />
      
      {/* Home page — cross-domain activity feed */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Suspense fallback={<DomainLoadingScreen domainName="Home" />}>
              <HomePage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Root redirect - use smart redirect to preserve domain context */}
      <Route path="/" element={<SmartRedirect />} />
      
      {/* Domain-based routes - each wrapped with AppLayout */}
      {availableRoutes.map((route) => {
        const RouteComponent = route.component;
        
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <AppLayout>
                <ProtectedRoute>
                  <div key={route.path} style={{ animation: 'fadeIn 120ms ease-in' }}>
                    <Suspense fallback={<DomainLoadingScreen domainName={route.name} />}>
                      <RouteComponent />
                    </Suspense>
                  </div>
                </ProtectedRoute>
              </AppLayout>
            }
          />
        );
      })}
      
      {/* Catch-all route - use smart redirect */}
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
}

// Auth Store Initializer that loads employee data for demo mode
function AuthStoreInitializer({ children }: { children: React.ReactNode }) {
  const { users: employees } = useSystemUsers();
  const { setAvailableEmployees } = useAuthUnified();

  // Initialize auth store and user cache on mount
  useEffect(() => {
    initializeAuthStore();
    const unsubUserCache = useUserCacheStore.getState().subscribe();

    return () => {
      cleanupAuthStore();
      unsubUserCache();
    };
  }, []);

  // Update available employees when they load
  useEffect(() => {
    if (employees && employees.length > 0) {
      setAvailableEmployees(employees);
    }
  }, [employees, setAvailableEmployees]);

  return <>{children}</>;
}

// Main App component
function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <TooltipProvider>
      <AuthStoreInitializer>
        <DemoSpendConfigProvider>
          <Router>
            <DomainProvider>
              <CommentModeProvider>
                {/* Track route changes for Google Analytics */}
                <RouteTracker />
                {/* Track user identification for Google Analytics */}
                <UserTracker />
                <AppRoutes />
                {/* Global comment system - renders on all pages */}
                <GenericCommentSystem />
              </CommentModeProvider>
            </DomainProvider>
          </Router>
        </DemoSpendConfigProvider>
      </AuthStoreInitializer>
      </TooltipProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;